/**
 * One customer, one vessel: which start slots a trip offers and whether a booking collides with
 * another departure on the same date. Pure and shared by the server and the booking forms, so both
 * refuse exactly the same combinations.
 *
 * - A departure is a trip at a start time. Bookings of the same trip with the same stored slot AND
 *   the same start share one departure; that is governed by trip.maxBookingsPerDay and is never an
 *   overlap (capacity counts by the stored slot, so a custom start that differs is another departure).
 * - Intervals are minutes after midnight on the booking's date; they are not compared across
 *   midnight (a trip running past 24:00 does not occupy the next date).
 * - The vessel is occupied from (start - bufferBefore) to (start + duration + bufferAfter).
 *   Intervals that only touch (one ends exactly when the next begins) do not overlap.
 */

import { BookingStatus, TimeSlot, TripType } from "./types";

/** Longest buffer a trip may set, in minutes. */
export const MAX_BUFFER_MINUTES = 240;
/** Duration assumed for a booking whose trip is unknown and which stored no duration. */
export const UNKNOWN_TRIP_DURATION_MINUTES = 180;

export interface OverlapTrip {
  id?: string;
  name?: string;
  tripType?: TripType;
  durationMinutes: number;
  timeSlots?: TimeSlot[];
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
}

export interface OverlapBooking {
  id?: string;
  tripId: string;
  date: string;
  timeSlot: TimeSlot | string;
  customStartTime?: string;
  customDurationHours?: number;
  tripDurationMinutes?: number;
  status?: BookingStatus | string;
}

export type TripsById = Map<string, OverlapTrip> | Record<string, OverlapTrip | undefined>;

/** The start slots a trip offers: its own list, else by type (short: morning + afternoon, full day: morning). */
export function allowedSlotsForTrip(trip: { tripType?: TripType; timeSlots?: TimeSlot[] }): TimeSlot[] {
  if (Array.isArray(trip.timeSlots) && trip.timeSlots.length > 0) return trip.timeSlots;
  return trip.tripType === TripType.FULL_DAY ? [TimeSlot.MORNING] : [TimeSlot.MORNING, TimeSlot.AFTERNOON];
}

/** "HH:mm" (a TimeSlot value or a custom start time) as minutes after midnight, or null if it is not a time. */
export function startMinutes(time: string | undefined | null): number | null {
  if (typeof time !== "string") return null;
  const m = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(time.trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** The vessel interval [from, to) in minutes after midnight for a trip starting at `start` (minutes). */
export function occupiedInterval(
  trip: Pick<OverlapTrip, "durationMinutes" | "bufferBeforeMinutes" | "bufferAfterMinutes">,
  start: number,
  durationMinutes: number = trip.durationMinutes,
): [number, number] {
  return [start - (trip.bufferBeforeMinutes ?? 0), start + durationMinutes + (trip.bufferAfterMinutes ?? 0)];
}

function lookup(tripsById: TripsById, tripId: string): OverlapTrip | undefined {
  return tripsById instanceof Map ? tripsById.get(tripId) : tripsById[tripId];
}

/** A booking's start in minutes: the admin's custom start time if set, else its slot. */
export function bookingStartMinutes(b: Pick<OverlapBooking, "timeSlot" | "customStartTime">): number | null {
  return startMinutes(b.customStartTime) ?? startMinutes(b.timeSlot);
}

/**
 * Duration of a booking in minutes: its custom duration if set, else the duration snapshot taken
 * when it was booked, else the trip's current duration, else 180. (A later edit of the trip's
 * duration therefore does not move existing bookings.)
 */
export function bookingDurationMinutes(b: OverlapBooking, tripsById: TripsById): number {
  if (b.customDurationHours && b.customDurationHours > 0) return Math.round(b.customDurationHours * 60);
  return b.tripDurationMinutes ?? lookup(tripsById, b.tripId)?.durationMinutes ?? UNKNOWN_TRIP_DURATION_MINUTES;
}

/** The trip's own time [start, start + duration) without buffers, or null if the start is not a time. */
export function bookingTripTimes(b: OverlapBooking, tripsById: TripsById): [number, number] | null {
  const start = bookingStartMinutes(b);
  return start === null ? null : [start, start + bookingDurationMinutes(b, tripsById)];
}

/**
 * The vessel interval of a booking (trip time plus the trip's buffers), or null if its start is not
 * a time. An unknown trip has no buffers.
 */
export function bookingInterval(b: OverlapBooking, tripsById: TripsById): [number, number] | null {
  const start = bookingStartMinutes(b);
  if (start === null) return null;
  const trip = lookup(tripsById, b.tripId);
  const duration = bookingDurationMinutes(b, tripsById);
  return occupiedInterval(trip ?? { durationMinutes: duration }, start, duration);
}

/** The stored slot for a custom start time: the slot at that time, else the latest slot not after it, else morning. */
export function slotForStart(start: string): TimeSlot {
  const minutes = startMinutes(start);
  const slots = (Object.values(TimeSlot) as TimeSlot[])
    .map((s) => ({ s, m: startMinutes(s)! }))
    .sort((a, b) => a.m - b.m);
  if (minutes === null) return TimeSlot.MORNING;
  let found = TimeSlot.MORNING;
  for (const { s, m } of slots) if (m <= minutes) found = s;
  return found;
}

/**
 * The first existing booking that keeps the vessel busy when `newBooking` is added: not cancelled,
 * same date, another departure (other trip or other start) and an overlapping interval. The booking
 * itself (same id) is skipped, so an edit does not collide with its own stored copy.
 */
export function findOverlap<B extends OverlapBooking>(
  newBooking: OverlapBooking,
  existingBookings: B[],
  tripsById: TripsById,
): B | undefined {
  return findAllOverlaps(newBooking, existingBookings, tripsById, true)[0];
}

/** Every existing booking that conflicts with `newBooking` under the rules of findOverlap (`firstOnly` stops at the first). */
export function findAllOverlaps<B extends OverlapBooking>(
  newBooking: OverlapBooking,
  existingBookings: B[],
  tripsById: TripsById,
  firstOnly = false,
): B[] {
  const found: B[] = [];
  const mine = bookingInterval(newBooking, tripsById);
  const myStart = bookingStartMinutes(newBooking);
  if (!mine || myStart === null) return found;
  for (const other of existingBookings) {
    if (other.status === BookingStatus.CANCELLED) continue;
    if (other.date !== newBooking.date) continue;
    if (newBooking.id && other.id === newBooking.id) continue;
    const otherStart = bookingStartMinutes(other);
    if (otherStart === null) continue;
    // Shared departure: same trip, same stored slot (what capacity counts) and same start
    if (other.tripId === newBooking.tripId && other.timeSlot === newBooking.timeSlot && otherStart === myStart) continue;
    const theirs = bookingInterval(other, tripsById);
    if (!theirs) continue;
    if (mine[0] < theirs[1] && theirs[0] < mine[1]) {
      found.push(other);
      if (firstOnly) return found;
    }
  }
  return found;
}

/**
 * A clock time as shown to people, by language: Swedish 24-hour "18:30", English 12-hour "6:30 PM".
 * Unreadable input is returned as is.
 */
export function formatClock(time: string, locale: "sv" | "en"): string {
  const m = startMinutes(time);
  if (m === null) return time;
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, "0");
  if (locale === "sv") return `${String(h).padStart(2, "0")}:${mm}`;
  return `${h % 12 || 12}:${mm} ${h >= 12 ? "PM" : "AM"}`;
}

/** English 12-hour clock ("10:00 AM", "6:30 PM"); used by the English API error messages. */
export function clockLabel12h(time: string): string {
  return formatClock(time, "en");
}

/** Minutes after midnight as "HH:mm" (wraps past midnight). */
export function formatMinutes(total: number): string {
  const m = ((Math.round(total) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/**
 * Check the scheduling fields of a trip create/update body. `null` means "clear the field" (PATCH)
 * and is accepted. Returns the problem in words, or undefined when the fields are fine.
 */
export function validateTripScheduling(fields: {
  timeSlots?: unknown;
  bufferBeforeMinutes?: unknown;
  bufferAfterMinutes?: unknown;
  partnerBookable?: unknown;
}): string | undefined {
  const { timeSlots, bufferBeforeMinutes, bufferAfterMinutes, partnerBookable } = fields;
  if (timeSlots !== undefined && timeSlots !== null) {
    const valid = Object.values(TimeSlot) as string[];
    if (
      !Array.isArray(timeSlots) ||
      timeSlots.length === 0 ||
      !timeSlots.every((s) => typeof s === "string" && valid.includes(s)) ||
      new Set(timeSlots).size !== timeSlots.length
    ) {
      return `timeSlots must be a non-empty list of distinct values from: ${valid.join(", ")}`;
    }
  }
  for (const [name, value] of [
    ["bufferBeforeMinutes", bufferBeforeMinutes],
    ["bufferAfterMinutes", bufferAfterMinutes],
  ] as const) {
    if (value === undefined || value === null) continue;
    if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > MAX_BUFFER_MINUTES) {
      return `${name} must be a whole number between 0 and ${MAX_BUFFER_MINUTES}`;
    }
  }
  if (partnerBookable !== undefined && partnerBookable !== null && typeof partnerBookable !== "boolean") {
    return "partnerBookable must be true or false";
  }
  return undefined;
}
